import React, { useCallback, useMemo } from 'react';

import { authentication } from '../../authentication';
import { ShareModal } from '../../sharing/ShareModal';
import { logger } from '../../logging';

import { TrackingTags } from '../actions';
import { useFilterAnalytics } from '../analytics/useFilterAnalytics';
import { updateFilter } from '../updateFilter';
import { Filter, FilterAccess, FilterPermissions, Market, UpdateFilterPermissionsRequest } from '../types';

import { SharingConfig } from './types';
import { AccessLevel, ShareUser } from '../../sharing/types';

export interface ShareFilterModalProps<T extends TrackingTags = TrackingTags> extends SharingConfig {
  isOpen: boolean;
  itemToShare?: Filter;
  getTrackingTags?: () => T;
  onClose: () => void;
  onSuccess?: (users: ShareUser[]) => void;
  onUsersLoadError?: (error: Error) => void;
  onShareError?: (error: Error) => void;
  preferencesApiBasePath?: string;
  context?: Market;
}
interface MappedPermissions extends Omit<FilterPermissions, 'access'> {
  access: AccessLevel;
}
interface MappedItemToShare extends Omit<Filter, 'permissions'> {
  permissions: MappedPermissions[];
}
const mapAccessLevel = (access: string): AccessLevel => (access === 'owner' ? AccessLevel.Owner : AccessLevel.Viewer);

export const ShareFilterModal = ({
  isOpen,
  itemToShare,
  getTrackingTags,
  getUsers,
  onClose,
  onSuccess,
  onShareError,
  preferencesApiBasePath,
  context,
}: ShareFilterModalProps): JSX.Element => {
  const { trackShareFilterModalElementClicked } = useFilterAnalytics();
  const owner = authentication.getUser();

  const mappedItemToShare: MappedItemToShare = useMemo(() => {
    return {
      ...itemToShare,
      permissions:
        itemToShare?.permissions.map(permission => ({
          ...permission,
          access: mapAccessLevel(permission.access),
        })) ?? [],
    };
  }, [itemToShare]);

  const getAvailableUsers = useCallback(async () => {
    const users = await getUsers();
    return users.map(user => ({
      userId: user.userId,
      givenName: user.givenName,
      familyName: user.familyName,
      email: user.email,
      clientId: owner.clientId,
    }));
  }, [getUsers, owner.clientId]);

  const shareUsers: ShareUser[] = useMemo(() => {
    return (
      mappedItemToShare?.permissions.map(permission => ({
        userId: permission.user.id,
        givenName: permission.user.givenName,
        familyName: permission.user.familyName,
        email: permission.user.email,
        accessLevel: permission.access,
        clientId: owner.clientId,
      })) ?? []
    );
  }, [mappedItemToShare.permissions, owner.clientId]);

  const handleTrack = useCallback(
    (shareItemName: string, shareWithTotal: number, shareAdded: number, shareRemoved: number) => {
      trackShareFilterModalElementClicked({
        getTrackingTags,
        shareItemName,
        filterName: shareItemName,
        shareWithTotal,
        shareAdded,
        shareRemoved,
      });
    },
    [getTrackingTags, trackShareFilterModalElementClicked]
  );

  const onFilterUpdateSuccess = useCallback(
    (users: ShareUser[]) => {
      onSuccess?.(users);
      const sharedWithBefore = mappedItemToShare.permissions?.filter(p => p.access !== AccessLevel.Owner);
      const sharedWithNow = users.filter(p => p.accessLevel !== AccessLevel.Owner);
      const removedUsers = sharedWithBefore?.filter(p => !sharedWithNow.some(u => u.userId === p.user.id));
      const addedUsers = sharedWithNow.filter(p => !sharedWithBefore?.some(u => u.user.id === p.userId));
      handleTrack(mappedItemToShare.name, sharedWithBefore.length, addedUsers.length, removedUsers.length);
    },
    [mappedItemToShare, handleTrack, onSuccess]
  );

  const onSave = useCallback(
    async (users: ShareUser[]) => {
      const updateRequest: UpdateFilterPermissionsRequest = {
        ...itemToShare,
        sharingContext: context,
        permissions: users.map(user => ({
          user_id: user.userId,
          access: user.accessLevel === AccessLevel.Owner ? FilterAccess.Owner : FilterAccess.View,
        })),
      };

      await updateFilter(updateRequest, preferencesApiBasePath);
      onFilterUpdateSuccess(users);
    },
    [context, itemToShare, preferencesApiBasePath, onFilterUpdateSuccess]
  );

  const handleShareError = useCallback(
    (e: Error) => {
      logger.error(e);
      onShareError && onShareError(e);
    },
    [onShareError]
  );

  const handleGetUsersError = useCallback(
    (err: Error) => {
      logger.error(err, { action: 'Get users to share filter', userId: owner.userId });
    },
    [owner.userId]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <ShareModal
      itemName={itemToShare?.name}
      getAvailableUsers={getAvailableUsers}
      users={shareUsers}
      onClose={handleClose}
      onShareError={handleShareError}
      onGetUsersError={handleGetUsersError}
      onSave={onSave}
      isOpen={isOpen}
      itemType="filter"
      sharableClients={[{ clientId: owner.clientId, clientName: owner.clientName }]}
    />
  );
};
